import {z} from "zod/v4"
import {location} from "@/schemas/location";
import {capitalize} from "../lib/capitalize";

z.config({
  customError: (iss) => {
    if (iss.code === "invalid_type" || (iss.code === "invalid_value")) {
      return `Required`;
    }
  },
});


export const notEmpty = z.string().trim().min(1, {message: "Required"});

export const step1Schema = z.object({
  firstName: z.string().pipe(notEmpty),
  lastName: z.string().pipe(notEmpty),
  email: z.email(),
  cellphoneCountryCode: z.string().pipe(notEmpty),
  cellphone: z.string().pipe(notEmpty),
  acceptTerms: z.literal(true, {
    error: () => ({message: "You must accept the terms and conditions",}),
  })
})


export enum Gender {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
}

export enum Privacy {
  PUBLIC = 'public',
  PRIVATE = 'private',
}

export const privacyOptions = Object.values(Privacy).map(g => ({label: capitalize(g), value: g}))


export const step2Schema = z.object({
  location: location,
  locationPrivacy: z.enum(Privacy),
  birthdate: z.date(),
  gender: z.enum(Gender),
  genderOther: z.string().optional(),
  genderPrivacy: z.enum(Privacy),
  introduction: z.string().max(600).optional(),
  yearPrivacy: z.enum(Privacy),
  monthPrivacy: z.enum(Privacy),
  dayPrivacy: z.enum(Privacy),
})

/**
 * Discriminates the shape of `account_requests.questionary` (a plain `JSON` column) so both the
 * extended questionnaire and the dummy one-question fallback can be told apart when read back.
 */
export enum QuestionaryType {
  EXTENDED = "extended",
  DUMMY = "dummy",
}

export const step3ExtendedSchema = z.object({
  alignmentIntention: z.object({
    drawsYou: z.string().trim().min(1, { message: "Required" }),
    missionResonance: z.string().trim().min(1, { message: "Required" }),
    coreValues: z.object({
      love: z.boolean().optional(),
      integrity: z.boolean().optional(),
      compassion: z.boolean().optional(),
      discretion: z.boolean().optional(),
      collaboration: z.boolean().optional(),
      creativity: z.boolean().optional(),
      freedomAutonomy: z.boolean().optional(),
      kindness: z.boolean().optional(),
      consciousCommunication: z.boolean().optional(),
      equityInclusion: z.boolean().optional(),
      transparency: z.boolean().optional(),
      sustainability: z.boolean().optional(),
      serviceToOthers: z.boolean().optional(),
      communityEmpowerment: z.boolean().optional(),
      innovation: z.boolean().optional(),
      accountability: z.boolean().optional(),
      personalGrowth: z.boolean().optional(),
      presenceMindfulness: z.boolean().optional(),
      gratitude: z.boolean().optional(),
      otherText: z.string().optional(),
      elaboration: z.string().optional(),
    })
  }),
  influenceReach: z.object({
    platforms: z.object({
      platform1: z.string().optional(),
      followers1: z.string().optional(),
      platform2: z.string().optional(),
      followers2: z.string().optional(),
      platform3: z.string().optional(),
      followers3: z.string().optional(),
    }).optional(),
    noSocialPlatforms: z.boolean().optional(),
    audience: z.string().optional(),
    credibility: z.string().optional(),
  }),
  contributionOnboarding: z.object({
    options: z.object({
      onboardBetas: z.boolean().optional(),
      onboardOrganisations: z.boolean().optional(),
      donateMoney: z.boolean().optional(),
      lendMoney: z.boolean().optional(),
      generateHype: z.boolean().optional(),
      marketing: z.boolean().optional(),
      projectManagement: z.boolean().optional(),
      webDevelopment: z.boolean().optional(),
      otherText: z.string().optional(),
    }),
    betaCapacity: z.string().optional(),
    betaStrategies: z.string().optional(),
    merchantCapacity: z.string().optional(),
    merchantStrategies: z.string().optional(),
    list5People: z.array(z.string().optional()).max(5).optional(),
  }),
  valueVision: z.object({
    collectives: z.string().optional(),
    uniqueSkills: z.string().optional(),
}),
  reflections: z.object({
    finalThoughts: z.string().optional(),
  }),
  improvements: z.string().optional(),
})

// Dummy questionnaire used for account types without a specific questionnaire defined yet
// (e.g. vendor, spark): a single free-text question, always optional.
export const step3DummySchema = z.object({
  response: z.string().trim().optional(),
})

export const step4EmailSchema = z.object({
  emailCode: z.string().min(6, "Email verification code must be 6 digits"),
})

export const step4CellphoneSchema = z.object({
  cellphoneCode: z.string().min(6, "Cellphone verification code must be 6 digits"),
})


export type Step1Data = z.infer<typeof step1Schema>
export type Step2Data = z.infer<typeof step2Schema>
export type Step3ExtendedData = z.infer<typeof step3ExtendedSchema>
export type Step3DummyData = z.infer<typeof step3DummySchema>
export type Step4EmailData = z.infer<typeof step4EmailSchema>
export type Step4CellphoneData = z.infer<typeof step4CellphoneSchema>

// Shape persisted in `account_requests.questionary`: the answered data plus which questionnaire it belongs to.
export type Step3ExtendedQuestionary = Step3ExtendedData & { type: QuestionaryType.EXTENDED }
export type Step3DummyQuestionary = Step3DummyData & { type: QuestionaryType.DUMMY }
export type Step3Questionary = Step3ExtendedQuestionary | Step3DummyQuestionary


